import React from 'react';
import { Search, BarChart2, RefreshCw, LineChart, ArrowRight } from 'lucide-react';

const HowIndexesWork = () => {
  const steps = [
    {
      step: 1,
      icon: Search,
      title: 'Asset Selection',
      description: 'Our analysts research and select the best-performing assets across Forex, CFD, and Crypto markets based on liquidity, volatility, and growth potential.'
    },
    {
      step: 2,
      icon: BarChart2,
      title: 'Weighted Allocation',
      description: 'Assets are weighted based on market capitalization, trading volume, or equal distribution to optimize risk-adjusted returns.'
    },
    {
      step: 3,
      icon: RefreshCw,
      title: 'Regular Rebalancing',
      description: 'Portfolios are automatically rebalanced periodically to maintain target allocations and adapt to market conditions.'
    },
    {
      step: 4,
      icon: LineChart,
      title: 'Performance Tracking',
      description: 'Track your index performance in real-time through our dashboard with detailed analytics and reporting.'
    }
  ];

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <span className="text-cyan-400 text-sm font-medium">How It Operates</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            How Our Index Funds Work
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A transparent, systematic approach to building and managing diversified investment portfolios.
          </p>
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent -translate-y-1/2" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="relative">
                  <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 h-full hover:border-cyan-500/50 transition-all duration-300 group hover:-translate-y-2">
                    {/* Step Number */}
                    <div className="absolute -top-4 left-8 w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-cyan-500/30">
                      {item.step}
                    </div>
                    
                    {/* Icon */}
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-6 mt-2 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-7 h-7 text-cyan-400" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                  
                  {/* Arrow */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-cyan-500/50" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Info Box */}
        <div className="mt-16 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 border border-cyan-500/20 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-white mb-2">0.5%</p>
              <p className="text-gray-400">Annual Management Fee</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">Weekly</p>
              <p className="text-gray-400">Rebalancing Frequency</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-white mb-2">$100</p>
              <p className="text-gray-400">Minimum Investment</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowIndexesWork;
